import {
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Text,
  Stack,
  Select,
  Flex,
} from '@mantine/core';
import React, { useEffect, useMemo } from 'react';
import { ParkingBillingSetting } from '../../models/parkingBillingSetting.ts';
import useSiteProviderAccountants from '../../hooks/useSiteProviderAccountants.ts';
import { useForm } from '@mantine/form';
import {
  endOfDay,
  endOfWeek,
  format,
  getDaysInMonth,
  setDate,
  startOfDay,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';

const periodOptions = [
  { value: '0', label: '當期' },
  { value: '1', label: '前 1 期' },
  { value: '2', label: '前 2 期' },
];

interface LaunchParkingBillingModalProps {
  opened: boolean;
  onClose: () => void;
  parkingBillingSetting: ParkingBillingSetting;
  loading: boolean;
  onConfirm: (params: {
    offset: number;
    accountantIds: string[];
    datePeriod: string;
  }) => void;
}

export const LaunchParkingBillingModal = ({
  opened,
  onClose,
  parkingBillingSetting,
  loading,
  onConfirm,
}: LaunchParkingBillingModalProps) => {
  const { isLoading: isAccountantsLoading, data: accountants } =
    useSiteProviderAccountants(parkingBillingSetting.siteProvider.id);

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      includeExternal: false,
      reportOffset: '0',
      accountantIds: parkingBillingSetting.args.accountantIds,
    },
  });

  useEffect(() => {
    if (accountants !== undefined) {
      const existIds = accountants.map((x) => x.id);
      form.setValues({
        accountantIds: parkingBillingSetting.args.accountantIds.filter((x) =>
          existIds.includes(x),
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountants, parkingBillingSetting.args.accountantIds]);

  const datePeriod = useMemo(() => {
    const reportSettingOffset =
      Number(parkingBillingSetting.args.reportPeriod.start.offset) + 1;
    const offset = Number(form.getValues().reportOffset) - reportSettingOffset;
    const reportPeriod = parkingBillingSetting.args.reportPeriod;
    const offsetUnit = reportPeriod.start.offsetUnit;
    const today = new Date();
    const startOfToday = startOfDay(today);
    const endOfToday = endOfDay(today);
    if (offsetUnit === 'month') {
      const firstDayOfMonth = setDate(
        subMonths(startOfToday, Number(offset) + 1),
        Number(reportPeriod.start.day),
      );
      const maxDay = getDaysInMonth(firstDayOfMonth);
      const day = Math.min(Number(reportPeriod.end.day), maxDay);

      const lastDayOfMonth = setDate(
        subMonths(endOfToday, Number(offset) + 1),
        day,
      );
      return `${format(firstDayOfMonth, 'yyyy-MM-dd')} ~ ${format(lastDayOfMonth, 'yyyy-MM-dd')}`;
    } else {
      const firstDayOfWeek = startOfWeek(
        subWeeks(startOfToday, Number(offset) + 1),
        {
          weekStartsOn: 1,
        },
      );
      const lastDayOfWeek = endOfWeek(
        subWeeks(endOfToday, Number(offset) + 1),
        {
          weekStartsOn: 1,
        },
      );
      return `${format(firstDayOfWeek, 'yyyy-MM-dd')} ~ ${format(lastDayOfWeek, 'yyyy-MM-dd')}`;
    }
  }, [form, parkingBillingSetting.args.reportPeriod]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="重產對帳單"
      centered
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      {isAccountantsLoading ? (
        <Loader />
      ) : (
        <Stack>
          <Text size="sm">預設會寄送給：parking_finance@autopass.xyz</Text>
          <form
            onSubmit={form.onSubmit((values) => {
              let accountantIds = [];
              if (values.includeExternal) {
                accountantIds = values.accountantIds;
              }
              const offset = Number(values.reportOffset);
              onConfirm({ offset, accountantIds, datePeriod });
            })}
          >
            <Flex direction="column" gap="md">
              <Checkbox
                label="是否需要寄送給廠商會計"
                key={form.key('includeExternal')}
                {...form.getInputProps('includeExternal')}
              />
              {form.getValues().includeExternal === true && (
                <MultiSelect
                  label="報表寄件對象"
                  placeholder="選擇 Email"
                  data={accountants?.map((accountant) => ({
                    value: accountant.id,
                    label: accountant.email,
                  }))}
                  key={form.key('accountantIds')}
                  {...form.getInputProps('accountantIds')}
                  searchable
                />
              )}

              <Select
                label="報表期別"
                data={periodOptions}
                key={form.key('reportOffset')}
                {...form.getInputProps('reportOffset')}
              />
              <Text size="sm" c="dimmed">
                報表日期：{datePeriod}
              </Text>
              <Group justify="flex-end">
                <Button variant="default" onClick={onClose}>
                  取消
                </Button>
                <Button loading={loading} type="submit">
                  送出
                </Button>
              </Group>
            </Flex>
          </form>
        </Stack>
      )}
    </Modal>
  );
};
